import { useState, useRef } from 'react';
import Papa from 'papaparse';
import { UploadSimple, CheckCircle, WarningCircle, X, FileArrowUp, ArrowRight, CircleNotch } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import api from '../lib/api';
import { toast } from 'sonner';

const STEP_UPLOAD  = 'upload';
const STEP_PREVIEW = 'preview';
const STEP_RESULT  = 'result';

// Normalize CSV header names → canonical field names
function normalizeHeaders(row) {
  const map = {
    'destination_url': 'destination_url', 'url': 'destination_url', 'dest': 'destination_url',
    'slug': 'slug', 'custom_slug': 'slug',
    'title': 'title', 'name': 'title',
    'tags': 'tags',
    'utm_source': 'utm_source', 'utm_medium': 'utm_medium',
    'utm_campaign': 'utm_campaign', 'utm_term': 'utm_term', 'utm_content': 'utm_content',
    'folder_id': 'folder_id',
  };
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    const norm = map[k.trim().toLowerCase()];
    if (norm) out[norm] = v;
  }
  return out;
}

function rowToPayload(norm) {
  const utm = {};
  if (norm.utm_source)   utm.source   = norm.utm_source;
  if (norm.utm_medium)   utm.medium   = norm.utm_medium;
  if (norm.utm_campaign) utm.campaign = norm.utm_campaign;
  if (norm.utm_term)     utm.term     = norm.utm_term;
  if (norm.utm_content)  utm.content  = norm.utm_content;
  return {
    destination_url: norm.destination_url || '',
    slug: norm.slug || '',
    title: norm.title || '',
    tags: norm.tags ? norm.tags.split('|').map(t => t.trim()).filter(Boolean) : [],
    utm_params: Object.keys(utm).length ? utm : undefined,
    folder_id: norm.folder_id || undefined,
  };
}

export default function BulkImportModal({ open, onOpenChange, onImportComplete }) {
  const [step, setStep] = useState(STEP_UPLOAD);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setStep(STEP_UPLOAD);
    setRows([]);
    setFileName('');
    setResult(null);
    setShowErrors(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const normalized = data.map(r => normalizeHeaders(r)).map(rowToPayload);
        setRows(normalized);
        setStep(STEP_PREVIEW);
      },
      error: () => toast.error('Failed to parse CSV'),
    });
    e.target.value = '';
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.post('/links/import', { rows, total_rows: rows.length });
      setResult(res.data);
      setStep(STEP_RESULT);
      if (res.data.success_count > 0) {
        onImportComplete?.();
      }
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const missingUrls = rows.filter(r => !r.destination_url).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-2xl rounded-2xl border border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <UploadSimple size={20} weight="duotone" className="text-primary" /> Bulk import links
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === STEP_UPLOAD && (
          <div className="space-y-5 mt-2">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file to create multiple links at once. Max <strong>500 rows</strong> per import.
            </p>

            {/* CSV format guide */}
            <div className="rounded-xl border border-border/80 bg-muted/20 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-2">CSV columns</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                {[
                  ['destination_url', 'Required. The URL to redirect to'],
                  ['slug', 'Optional. Custom short code'],
                  ['title', 'Optional. Link name'],
                  ['tags', 'Optional. Separate with |'],
                  ['utm_source', 'Optional. UTM source'],
                  ['utm_campaign', 'Optional. UTM campaign'],
                ].map(([col, desc]) => (
                  <div key={col} className="flex gap-2">
                    <span className="font-mono text-primary shrink-0">{col}</span>
                    <span className="text-muted-foreground">{desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Drop zone */}
            <label
              htmlFor="csv-upload"
              className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-border/60 bg-muted/10 px-6 py-10 cursor-pointer hover:border-primary/40 hover:bg-primary/[0.02] transition-colors"
            >
              <FileArrowUp size={36} weight="duotone" className="text-primary/60" />
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">Click to upload or drag & drop</p>
                <p className="text-xs text-muted-foreground mt-0.5">CSV files only</p>
              </div>
              <input id="csv-upload" ref={fileRef} type="file" accept=".csv,text/csv" className="sr-only" onChange={handleFileChange} />
            </label>

            {/* Sample download */}
            <p className="text-xs text-muted-foreground text-center">
              Need a template?{' '}
              <a
                href="data:text/csv;charset=utf-8,destination_url,slug,title,tags,utm_source,utm_campaign%0Ahttps://example.com/product,my-product,My Product,sale|summer,newsletter,summer-sale%0Ahttps://example.com/blog,,Blog Post,,twitter,organic"
                download="linkflow-import-template.csv"
                className="text-primary hover:underline font-medium"
              >
                Download sample CSV
              </a>
            </p>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === STEP_PREVIEW && (
          <div className="space-y-4 mt-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{rows.length}</span> rows detected from <span className="font-mono text-xs">{fileName}</span>
              </p>
              <button onClick={() => setStep(STEP_UPLOAD)} className="text-xs text-muted-foreground hover:text-foreground">
                Change file
              </button>
            </div>

            {missingUrls > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                <WarningCircle size={16} weight="fill" className="text-amber-500 shrink-0" />
                <span><strong>{missingUrls}</strong> row{missingUrls > 1 ? 's' : ''} missing a destination URL and will be skipped</span>
              </div>
            )}

            {rows.length > 500 && (
              <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800">
                <WarningCircle size={16} weight="fill" className="text-rose-500 shrink-0" />
                <span>Only the first 500 rows will be imported</span>
              </div>
            )}

            {/* Preview table */}
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto max-h-64">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/60">
                    <tr className="border-b border-border">
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">destination_url</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">slug</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">title</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">tags</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {rows.slice(0, 10).map((row, i) => (
                      <tr key={i} className={`${!row.destination_url ? 'bg-amber-50' : 'hover:bg-muted/30'} transition-colors`}>
                        <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                        <td className="px-3 py-2 font-mono max-w-[200px] truncate text-foreground">{row.destination_url || <span className="text-amber-600">missing</span>}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">{row.slug || <span className="italic opacity-50">auto</span>}</td>
                        <td className="px-3 py-2 text-muted-foreground max-w-[120px] truncate">{row.title || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{row.tags?.join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 10 && (
                <div className="px-3 py-2 border-t border-border bg-muted/20 text-xs text-muted-foreground">
                  + {rows.length - 10} more rows not shown
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-1">
              <Button variant="outline" onClick={handleClose} className="rounded-xl border-border">Cancel</Button>
              <Button onClick={handleImport} disabled={importing || rows.length === 0} className="rounded-xl gradient-primary text-white shadow-glow font-semibold gap-2">
                {importing ? <CircleNotch size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                {importing ? 'Importing…' : `Import ${Math.min(rows.length, 500)} links`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === STEP_RESULT && result && (
          <div className="space-y-4 mt-2">
            {result.success_count > 0 && (
              <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <CheckCircle size={24} weight="fill" className="text-emerald-500 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800">{result.success_count} link{result.success_count !== 1 ? 's' : ''} created successfully</p>
                  {result.failure_count > 0 && <p className="text-xs text-emerald-700 mt-0.5">{result.failure_count} rows had errors</p>}
                </div>
              </div>
            )}
            {result.success_count === 0 && (
              <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
                <WarningCircle size={24} weight="fill" className="text-rose-500 shrink-0" />
                <p className="text-sm font-semibold text-rose-800">Import failed — no links were created</p>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div>
                <button
                  onClick={() => setShowErrors(v => !v)}
                  className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={12} /> {showErrors ? 'Hide' : 'Show'} {result.errors.length} error{result.errors.length !== 1 ? 's' : ''}
                </button>
                {showErrors && (
                  <div className="mt-2 rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr className="border-b border-border">
                          <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Row</th>
                          <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Slug</th>
                          <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {result.errors.map((err, i) => (
                          <tr key={i} className="text-rose-700">
                            <td className="px-3 py-2">{err.row}</td>
                            <td className="px-3 py-2 font-mono">{err.slug || '—'}</td>
                            <td className="px-3 py-2">{err.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end pt-1">
              <Button variant="outline" onClick={reset} className="rounded-xl border-border">Import another</Button>
              <Button onClick={handleClose} className="rounded-xl gradient-primary text-white shadow-glow font-semibold">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
